import { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, AlertCircle } from "lucide-react";

export default function ResetPassword() {
    const { token } = useParams();

    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");

    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setMessage("");

        if (!email) {
            setError("Invalid password reset link.");
            return;
        }

        if (!token) {
            setError("Invalid or expired password reset link.");
            return;
        }

        if (!password || !confirmPassword) {
            setError("Please enter your new password.");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                "https://uaediscountedhotels.com/api/reset-password",
                {
                    token,
                    email,
                    password,
                    password_confirmation: confirmPassword,
                }
            );

            if (response.data.success) {
                setMessage(
                    "Password reset successfully. Redirecting to login..."
                );

                setPassword("");
                setConfirmPassword("");

                setTimeout(() => {
                    navigate("/");
                }, 3000);
            } else {
                setError(
                    response.data.message ||
                    "Unable to reset password."
                );
            }
        } catch (err) {
            console.error("Reset password error:", err);

            setError(
                err.response?.data?.message ||
                "Unable to reset password. The reset link may have expired."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-orange-50 px-4 py-10">

            {/* Background Decorations */}
            <div className="fixed -top-32 -left-32 w-96 h-96 bg-[#D4A83F]/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="fixed -bottom-40 -right-40 w-[450px] h-[450px] bg-[#174B5F]/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative w-full max-w-md">

                {/* Card */}
                <div className="bg-white rounded-[32px] shadow-[0_25px_70px_rgba(15,23,42,0.12)] border border-slate-100 overflow-hidden">

                    {/* Top Accent */}
                    <div className="h-1.5 bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F]"></div>

                    <div className="p-7 sm:p-9">

                        {/* Icon */}
                        <div className="flex justify-center mb-5">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#002B45] via-[#174B5F] to-[#D4A83F] flex items-center justify-center shadow-lg">
                                <Lock className="w-7 h-7 text-white" />
                            </div>
                        </div>

                        {/* Heading */}
                        <div className="text-center mb-7">

                            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#D4A83F]/15 text-[#174B5F] text-xs font-semibold mb-4">
                                Account Security
                            </span>

                            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                                Reset Password
                            </h1>

                            <p className="text-sm text-slate-500 mt-2 leading-6">
                                Create a new password for your account.
                            </p>

                        </div>

                        {/* Error */}
                        {error && (
                            <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl text-sm">

                                <AlertCircle
                                    size={19}
                                    className="flex-shrink-0 mt-0.5"
                                />

                                <p>{error}</p>

                            </div>
                        )}

                        {/* Success */}
                        {message && (
                            <div className="mb-5 flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 p-4 rounded-2xl text-sm">

                                <CheckCircle
                                    size={19}
                                    className="flex-shrink-0 mt-0.5"
                                />

                                <p>{message}</p>

                            </div>
                        )}

                        {/* Form */}
                        <form
                            onSubmit={handleSubmit}
                            className="space-y-5"
                        >

                            {/* New Password */}
                            <div>

                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    New Password
                                </label>

                                <div className="relative">

                                    <Lock
                                        size={18}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        placeholder="Enter new password"
                                        disabled={loading}
                                        className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 outline-none transition focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10 disabled:opacity-60"
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#174B5F] transition"
                                    >
                                        {showPassword ? (
                                            <EyeOff size={18} />
                                        ) : (
                                            <Eye size={18} />
                                        )}
                                    </button>

                                </div>

                            </div>

                            {/* Confirm Password */}
                            <div>

                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Confirm Password
                                </label>

                                <div className="relative">

                                    <Lock
                                        size={18}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type={
                                            showConfirmPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={confirmPassword}
                                        onChange={(e) =>
                                            setConfirmPassword(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Confirm new password"
                                        disabled={loading}
                                        className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 outline-none transition focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10 disabled:opacity-60"
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowConfirmPassword(
                                                !showConfirmPassword
                                            )
                                        }
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#174B5F] transition"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff size={18} />
                                        ) : (
                                            <Eye size={18} />
                                        )}
                                    </button>

                                </div>

                            </div>

                            {/* Password Requirement */}
                            <div className="bg-slate-50 rounded-xl px-4 py-3">

                                <p className="text-xs text-slate-500">
                                    Password must contain at least 6
                                    characters.
                                </p>

                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 px-6 bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F] hover:shadow-[0_15px_35px_rgba(23,75,95,0.25)] hover:-translate-y-0.5 text-white rounded-xl font-semibold flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >

                                {loading ? (
                                    <>
                                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        Reset Password
                                        <ArrowRight size={18} />
                                    </>
                                )}

                            </button>

                        </form>

                        {/* Back to Login */}
                        <div className="text-center mt-6">

                            <button
                                type="button"
                                onClick={() => navigate("/")}
                                className="text-sm font-semibold text-[#174B5F] hover:text-[#D4A83F] transition"
                            >
                                ← Back to Login
                            </button>

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
}